import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "mobile_token";
const USER_KEY = "mobile_user";
const EMAIL_KEY = "mobile_email";

export const mobileAuth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || "null");
    } catch {
      return null;
    }
  },
  getRememberedEmail: () => localStorage.getItem(EMAIL_KEY),
  setAuth: (token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    if (user?.email) localStorage.setItem(EMAIL_KEY, user.email);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${mobileAuth.getToken()}` },
});

export const api = {
  loginPassword: (email, password) =>
    axios.post(`${API}/auth/login`, { email, password }).then((r) => r.data),
  loginPin: (email, pin) =>
    axios.post(`${API}/auth/pin/login`, { email, pin }).then((r) => r.data),
  pinStatus: () => axios.get(`${API}/auth/pin/status`, authHeaders()).then((r) => r.data),
  setPin: (pin) =>
    axios.post(`${API}/auth/pin/set`, { pin }, authHeaders()).then((r) => r.data),
  me: () => axios.get(`${API}/auth/me`, authHeaders()).then((r) => r.data),
  permissions: () =>
    axios.get(`${API}/auth/me/permissions`, authHeaders()).then((r) => r.data),

  productByCode: (code) =>
    axios.get(`${API}/products/by-code?code=${encodeURIComponent(code)}`, authHeaders()).then((r) => r.data),
  poPicker: (productCode) =>
    axios
      .get(`${API}/pallet/po-picker?product_code=${encodeURIComponent(productCode)}`, authHeaders())
      .then((r) => r.data),

  pair: ({ pallet_id, product_code, po_id, device_id, target_qty_override, override_reason }) =>
    axios
      .post(
        `${API}/pallet/pair`,
        { pallet_id, product_code, po_id: po_id || null, device_id, target_qty_override: target_qty_override ?? null, override_reason: override_reason || null },
        authHeaders(),
      )
      .then((r) => r.data),

  produce: (pallet_id, actual_qty, reason) =>
    axios
      .post(
        `${API}/pallet/${pallet_id}/produce`,
        { actual_qty, reason },
        authHeaders(),
      )
      .then((r) => r.data),

  getPallet: (pallet_id) =>
    axios.get(`${API}/pallet/${pallet_id}`, authHeaders()).then((r) => r.data),

  voidPallet: (pallet_id, reason) =>
    axios
      .post(`${API}/pallet/${pallet_id}/void`, { reason }, authHeaders())
      .then((r) => r.data),

  packPallet: (pallet_id, device_id) =>
    axios
      .post(`${API}/pallet/${pallet_id}/pack`, { device_id }, authHeaders())
      .then((r) => r.data),

  unpackPallet: (pallet_id, reason) =>
    axios
      .post(`${API}/pallet/${pallet_id}/unpack`, { reason }, authHeaders())
      .then((r) => r.data),

  // Change a pallet's PO (or unassign with po_id=null). Requires pallet.reassign_po.
  reassignPo: (pallet_id, po_id, reason) =>
    axios
      .post(`${API}/pallet/${pallet_id}/reassign-po`, { po_id: po_id || null, reason }, authHeaders())
      .then((r) => r.data),

  // Build a URL the operator can open/download in a new tab — embeds the bearer
  // token via the auth header isn't possible for window.open, so we fetch blob.
  // A timestamp cache-buster prevents stale PDFs from being served by the
  // browser / any intermediate CDN after a backend rollout.
  cardPdfBlob: async (pallet_id) => {
    const res = await axios.get(`${API}/pallet/${pallet_id}/card.pdf?t=${Date.now()}`, {
      ...authHeaders(),
      responseType: "blob",
    });
    return res.data;
  },

  // List the current operator's recent pallets (newest first). Powers "My Pallets".
  myRecent: ({ state, limit = 50 } = {}) => {
    const q = new URLSearchParams();
    if (state) q.set("state", state);
    if (limit) q.set("limit", String(limit));
    const qs = q.toString();
    return axios
      .get(`${API}/pallet/my-recent${qs ? `?${qs}` : ""}`, authHeaders())
      .then((r) => r.data);
  },

  // List ALL operators' pallets (newest first). Requires pallet.view (super admin has it).
  // Powers the "All operators" scope on the mobile My Pallets screen.
  allPallets: ({ state, limit = 100 } = {}) => {
    const q = new URLSearchParams();
    if (state) q.set("state", state);
    if (limit) q.set("limit", String(limit));
    const qs = q.toString();
    return axios
      .get(`${API}/pallets${qs ? `?${qs}` : ""}`, authHeaders())
      .then((r) => r.data);
  },

  liveProduction: ({ date_from, date_to, group_by = "product" } = {}) => {
    const q = new URLSearchParams();
    if (date_from) q.set("date_from", date_from);
    if (date_to) q.set("date_to", date_to);
    if (group_by) q.set("group_by", group_by);
    return axios
      .get(`${API}/production/live-summary?${q.toString()}`, authHeaders())
      .then((r) => r.data);
  },

  // Live Production drill-down: individual pallets for a product/PO + attached DPR.
  liveProductionPallets: ({ product_code, po_no, date_from, date_to } = {}) => {
    const q = new URLSearchParams();
    if (product_code != null) q.set("product_code", product_code);
    if (po_no != null) q.set("po_no", po_no);
    if (date_from) q.set("date_from", date_from);
    if (date_to) q.set("date_to", date_to);
    return axios
      .get(`${API}/production/live-pallets?${q.toString()}`, authHeaders())
      .then((r) => r.data);
  },

  // Live inventory dashboard data (KPIs + per-product rows). Requires inventory.view.
  // Pass { withValue: true } to also request USD/INR valuation — the backend only
  // returns value fields if the user has the inventory.view_value permission.
  inventoryCurrent: ({ withValue = false } = {}) =>
    axios
      .get(`${API}/inventory/current${withValue ? "?with_value=1" : ""}`, authHeaders())
      .then((r) => r.data),

  // Source-level drill-down for one product (produced / dispatched / opening / adjustments).
  inventoryBreakdown: (code) =>
    axios
      .get(`${API}/inventory/product/${encodeURIComponent(code)}/breakdown`, authHeaders())
      .then((r) => r.data),

  // ── Scan-based physical stock count ──
  scListSessions: () =>
    axios.get(`${API}/stock-count/sessions`, authHeaders()).then((r) => r.data),
  scCreateSession: (name) =>
    axios.post(`${API}/stock-count/sessions`, { name }, authHeaders()).then((r) => r.data),
  scGetSession: (id) =>
    axios.get(`${API}/stock-count/sessions/${id}`, authHeaders()).then((r) => r.data),
  scScan: (id, pallet_id, zone, capture_method) =>
    axios.post(`${API}/stock-count/sessions/${id}/scan`, { pallet_id, zone: zone || undefined, capture_method }, authHeaders()).then((r) => r.data),
  scManual: (id, body) =>
    axios.post(`${API}/stock-count/sessions/${id}/manual`, body, authHeaders()).then((r) => r.data),
  scProductHint: (product_code) =>
    axios.get(`${API}/stock-count/product-hint?product_code=${encodeURIComponent(product_code)}`, authHeaders()).then((r) => r.data),
  scResolveShort: (short) =>
    axios.get(`${API}/stock-count/resolve-short?short=${encodeURIComponent(short)}`, authHeaders()).then((r) => r.data),
  scListZones: () =>
    axios.get(`${API}/stock-count/zones`, authHeaders()).then((r) => r.data),
  productCodes: () =>
    axios.get(`${API}/stock-count/product-codes`, authHeaders()).then((r) => r.data?.codes || []),
  scFinalize: (id) =>
    axios.post(`${API}/stock-count/sessions/${id}/finalize`, {}, authHeaders()).then((r) => r.data),
  scUndoLast: (id) =>
    axios.delete(`${API}/stock-count/sessions/${id}/scan/last`, authHeaders()).then((r) => r.data),
  scSkip: (id, zone, delta) =>
    axios.post(`${API}/stock-count/sessions/${id}/skip`, { zone, delta }, authHeaders()).then((r) => r.data),
  scAddUnidentified: (id, blobs, zone, note) => {
    const fd = new FormData();
    (Array.isArray(blobs) ? blobs : [blobs]).forEach((b, i) =>
      fd.append("photos", b, `unidentified-${Date.now()}-${i}.jpg`));
    fd.append("zone", zone);
    if (note) fd.append("note", note);
    return axios.post(`${API}/stock-count/sessions/${id}/unidentified`, fd, authHeaders()).then((r) => r.data);
  },
  scDeleteUnidentified: (id, uid) =>
    axios.delete(`${API}/stock-count/sessions/${id}/unidentified/${uid}`, authHeaders()).then((r) => r.data),
  scResolveUnidentified: (id, uid, payload) =>
    axios.post(`${API}/stock-count/sessions/${id}/unidentified/${uid}/resolve`, payload, authHeaders()).then((r) => r.data),
  scResolveUnidentifiedNote: (id, uid, note) =>
    axios.post(`${API}/stock-count/sessions/${id}/unidentified/${uid}/resolve-note`, { note }, authHeaders()).then((r) => r.data),
  scResolveUnidentifiedPallet: (id, uid, payload) =>
    axios.post(`${API}/stock-count/sessions/${id}/unidentified/${uid}/resolve-pallet`, payload, authHeaders()).then((r) => r.data),
  scPhotoUrl: (fileId) => `${API}/files/download/${fileId}?token=${mobileAuth.getToken()}&inline=true`,
  scExportCsvBlob: async (id) => {
    const res = await axios.get(`${API}/stock-count/sessions/${id}/export-csv?t=${Date.now()}`, {
      ...authHeaders(),
      responseType: "blob",
    });
    return res.data;
  },
  scDeleteSession: (id) =>
    axios.delete(`${API}/stock-count/sessions/${id}`, authHeaders()).then((r) => r.data),
};

// ---- Offline cache for the view-only mobile Inventory screens ----
// Stores the last successful payload so operators on the shop floor can still
// glance at last-known stock when the network drops.
const INV_CACHE_CURRENT = "inv_cache_current";
const INV_CACHE_BD_PREFIX = "inv_cache_bd_";

function _writeCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    /* quota / private mode — ignore */
  }
}

function _readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null; // { data, ts }
  } catch {
    return null;
  }
}

export const invCache = {
  getCurrent: () => _readCache(INV_CACHE_CURRENT),
  setCurrent: (data) => _writeCache(INV_CACHE_CURRENT, data),
  getBreakdown: (code) => _readCache(INV_CACHE_BD_PREFIX + code),
  setBreakdown: (code, data) => _writeCache(INV_CACHE_BD_PREFIX + code, data),
};

/**
 * Parse a product QR payload (URL like https://hi.faultreport.in/?code=ABC-123-XYZ-20260211-001
 * or a bare code) into the product code.
 *
 * Existing ERP QRs use the format `{product_code}-{YYYYMMDD}-{batch_number}` for the `code`
 * query param. Product codes can contain hyphens, so we anchor on the trailing date+batch.
 */
export function extractProductCode(payload) {
  if (!payload) return null;
  let raw = String(payload).trim();

  // If it looks like a URL, pull the code query param
  try {
    if (raw.startsWith("http://") || raw.startsWith("https://")) {
      const u = new URL(raw);
      const code = u.searchParams.get("code");
      if (code) raw = code;
    }
  } catch {
    /* fall through; treat as bare */
  }

  // Match either full {code}-{YYYYMMDD}-{batch} or a bare code
  const m = raw.match(/^(.+)-(\d{8})-(\d+)$/);
  if (m) return m[1];

  // Otherwise: bare product code (no date/batch suffix)
  return raw;
}

/**
 * Returns true if the payload is a valid pallet QR (PLT-YYYY-NNNNNN).
 */
export function isPalletId(payload) {
  return /^PLT-\d{4}-\d{6}$/.test(String(payload || "").trim());
}
